import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { CircularProgress } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { subscribeSaves } from 'utils/Axios/saveTracker';

const useStyles = makeStyles(() => ({
    root: {
        position: 'fixed',
        left: 16,
        bottom: 16,
        // Above everything, the category spread over the whole window included.
        zIndex: 1400,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderRadius: 16,
        fontSize: 13,
        fontWeight: 600,
        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
        transition: 'opacity 0.4s',
        pointerEvents: 'none',
    },
    saving: { background: '#fff8e1', color: '#8d6e00' },
    saved: { background: '#e8f5e9', color: '#1b5e20' },
    failed: { background: '#ffebee', color: '#b71c1c', pointerEvents: 'auto' },
    quiet: { opacity: 0.55 },
}));

const QUIET_AFTER_MS = 4000;

const time = date => date.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

/**
 * «ЗБЕРЕЖЕНО» — whether what the secretary just entered reached the server
 * (organiser, 27 Oct 2026). Fed by every write the app sends (saveTracker),
 * so a fight's points, a shooting score, a tie order and a setting all report
 * the same way. Nothing until the first save; then «Зберігається…», «✓
 * Збережено о 10:23:41» (dimmed after a few seconds, never gone), or a red
 * «Не збережено» that stays until a save succeeds.
 */
const SaveStatus = () => {
    const classes = useStyles();
    const [t] = useTranslation();
    const [state, setState] = useState(null);
    const [quiet, setQuiet] = useState(false);

    useEffect(() => subscribeSaves(next => {
        setState(next);
        setQuiet(false);
    }), []);

    useEffect(() => {
        if (!state || state.pending > 0 || state.failed || !state.savedAt) {
            return undefined;
        }
        const timer = setTimeout(() => setQuiet(true), QUIET_AFTER_MS);

        return () => clearTimeout(timer);
    }, [state]);

    // Never on a hall screen — the wall is not where anybody saves anything —
    // nor on the mat phone, which says it in its own top bar: down here it lay
    // over «Розпочати поєдинок» (organiser, 3 Nov 2026).
    if (!state || !state.seen || /^\/(display|mat-phone)(\/|$)/.test(window.location.pathname)) {
        return null;
    }

    if (state.pending > 0) {
        return (
            <div className={`${classes.root} ${classes.saving}`} data-testid='save-status' data-state='saving' role='status'>
                <CircularProgress size={12} color='inherit' />
                {t('saveStatus.saving')}
            </div>
        );
    }
    if (state.failed) {
        return (
            <div className={`${classes.root} ${classes.failed}`} data-testid='save-status' data-state='failed' role='alert'>
                {`✗ ${t('saveStatus.failed')}`}
            </div>
        );
    }

    return (
        <div className={`${classes.root} ${classes.saved} ${quiet ? classes.quiet : ''}`} data-testid='save-status' data-state='saved' role='status'>
            {`✓ ${t('saveStatus.saved', { time: state.savedAt ? time(state.savedAt) : '' })}`}
        </div>
    );
};

export default SaveStatus;
